import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { isPasswordValid } from "../utils/password";
import { PasswordChecklist } from "../Components/PasswordChecklist/PasswordChecklist";
import { OtpInput } from "../Components/OtpInput/OtpInput";
import { useUser } from "../context/UserContext";

const API = process.env.REACT_APP_BACKEND_URL;

// RCS external-client login.
//
// Flow:
//   login  → email + password verified  (POST /auth/login/password/verify)
//   totp   → authenticator code → sign in  (POST /auth/login/password)
//   forgot → email → emailed OTP  (POST /auth/login/forgot-password)
//   reset  → OTP + new password ×2  (POST /auth/login/reset-password)
//
// Password is validated first; only on success does the authenticator screen
// appear. A freshly added RCS user has password: null, so first-time users must
// go through the forgot/reset flow before they can sign in.
export const Login = () => {
  const navigate = useNavigate();
  const { setUser } = useUser();

  // step: "login" | "totp" | "forgot" | "reset"
  const [step, setStep] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");     // login step
  const [authCode, setAuthCode] = useState("");     // login step (authenticator)
  const [otp, setOtp] = useState("");               // reset step
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const headers = { "Content-Type": "application/json" };
  const clearMessages = () => { setError(""); setInfo(""); };

  // Populate UserContext (and its localStorage cache) right after login so the
  // header shows the user immediately. The provider only fetches once at mount —
  // before the token existed — so without this the name stays blank until reload.
  const hydrateUser = async (token) => {
    try {
      const res = await fetch(`${API}/API/V2/users/me`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      localStorage.setItem("user", JSON.stringify(data));
      localStorage.setItem("username", data.email || "");
      setUser({
        username: data.email || "",
        isPartner: data.isPartner ?? null,
        isPMO: data.isPMO ?? false,
        name: data.name ?? "",
        team: data.team ?? "",
        usertype: data.usertype ?? "",
        role: data.role ?? {},
        canApproveLoa: data.canApproveLoa ?? false,
        loaded: true,
      });
    } catch {
      /* non-fatal — provider will hydrate on the next load */
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(20);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

  const resetResetFields = () => { setOtp(""); setNewPassword(""); setConfirmPassword(""); };

  /* ── step: login (verify email + password) ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const res = await fetch(`${API}/API/V2/auth/login/password/verify`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.code === "C00") {
        setAuthCode("");
        setStep("totp");
      } else {
        setError(data.message || "Invalid email or password.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  /* ── step: totp (verify authenticator code, then sign in) ── */
  const handleTotp = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const res = await fetch(`${API}/API/V2/auth/login/password`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email, password, otp: authCode }),
      });
      const data = await res.json();
      if (data.code === "C00" && data.token) {
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("jassoVerified", false);
        await hydrateUser(data.token);
        navigate("/Dashboard");
      } else {
        setError(data.message || "Invalid authenticator code.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  /* ── step: forgot (request OTP) ── */
  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const res = await fetch(`${API}/API/V2/auth/login/forgot-password`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.code === "C00") {
        setInfo("If an account exists for that email, a 6-digit code has been sent.");
        setStep("reset");
        startResendCooldown();
      } else {
        setError(data.message || "Failed to send verification code. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  /* ── step: reset (verify OTP + set password) ── */
  const handleResetPassword = async (e) => {
    e.preventDefault();
    clearMessages();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!isPasswordValid(newPassword)) {
      setError("Password does not meet all the requirements below.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/API/V2/auth/login/reset-password`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email, otp, password: newPassword, confirmPassword }),
      });
      const data = await res.json();
      if (data.code === "C00") {
        resetResetFields();
        setPassword("");
        setStep("login");
        setInfo("Password set successfully. Please sign in.");
      } else {
        setError(data.message || "Could not set password. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const goToForgot = () => { clearMessages(); resetResetFields(); setStep("forgot"); };
  const goToLogin = () => { clearMessages(); resetResetFields(); setAuthCode(""); setStep("login"); };

  /* ─────────────────────── render ────────────────────────── */
  return (
    <div className="min-h-screen flex items-center justify-center bg-jiojbm20 px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header bar */}
        <div className="bg-jiojbm60 px-8 py-5">
          <h1 className="text-white text-xl font-bold tracking-tight">Sandesh NNI Support </h1>
          <p className="text-jiojbm25 text-xs mt-0.5">Sign In to your account</p>
        </div>

        <div className="px-8 py-8 space-y-6">
          {/* Error / Info banners */}
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              <span className="mt-px">⚠</span>
              <span>{error}</span>
            </div>
          )}
          {info && (
            <div className="flex items-start gap-2 px-4 py-3 bg-green-50 text-green-700 text-sm rounded-lg border border-green-200">
              <span className="mt-px">✓</span>
              <span>{info}</span>
            </div>
          )}

          {/* ── Step: Login ── */}
          {step === "login" && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-jiojbm40 focus:border-transparent transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-jiojbm40 focus:border-transparent transition"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                {loading ? "Verifying…" : "Continue"}
              </button>
              <button
                type="button"
                onClick={goToForgot}
                className="block w-full text-xs text-jiojbm60 hover:text-jiojbm70 transition-colors"
              >
                Forgot your password? Set it up →
              </button>
            </form>
          )}

          {/* ── Step: TOTP (authenticator code) ── */}
          {step === "totp" && (
            <form onSubmit={handleTotp} className="space-y-5">
              <p className="text-sm text-gray-600">
                Enter the 6-digit code from your authenticator app to finish signing
                in as <span className="font-medium text-gray-700">{email}</span>.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Authenticator Code</label>
                <OtpInput value={authCode} onChange={setAuthCode} autoFocus />
              </div>
              <button
                type="submit"
                disabled={loading || authCode.length !== 6}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
              <button
                type="button"
                onClick={goToLogin}
                className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                ← Back
              </button>
            </form>
          )}

          {/* ── Step: Forgot (request OTP) ── */}
          {step === "forgot" && (
            <form onSubmit={handleSendOtp} className="space-y-5">
              <p className="text-sm text-gray-600">
                Enter your email and we'll send you a verification code to set a new password.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-jiojbm40 focus:border-transparent transition"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                {loading ? "Sending…" : "Send Code"}
              </button>
              <button
                type="button"
                onClick={goToLogin}
                className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                ← Back to sign in
              </button>
            </form>
          )}

          {/* ── Step: Reset (verify OTP + set password) ── */}
          {step === "reset" && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <p className="text-sm text-gray-600">
                Enter the 6-digit code sent to{" "}
                <span className="font-medium text-gray-700">{email}</span> and choose a new password.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Verification Code</label>
                <OtpInput value={otp} onChange={setOtp} autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-jiojbm40 focus:border-transparent transition"
                />
                <PasswordChecklist value={newPassword} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-jiojbm40 focus:border-transparent transition"
                />
              </div>
              <button
                type="submit"
                disabled={loading || otp.length !== 6 || !isPasswordValid(newPassword) || newPassword !== confirmPassword}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                {loading ? "Saving…" : "Set Password"}
              </button>
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={loading || resendCooldown > 0}
                className="block w-full text-xs text-jiojbm60 hover:text-jiojbm70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : "Resend code"}
              </button>
              <button
                type="button"
                onClick={goToLogin}
                className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                ← Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
